import React, { useState } from 'react';
import {
  FDBox,
  FDContentContainer, FDEmptyState, FDHeadlineText, FDClickableText,
  FDPaper, FDPrimaryActionIconButton, FDSearchInput, FDStack,
  FDTable, FDTableBody, FDTableCell, FDTableHead, FDTableRow, FDSecondaryActionIconButton,
} from '@facet-wealth/design';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { usePaginatedCollectionStateContext } from 'Context/PagedCollectionStateContext';
import { useFQAdminContext } from 'Views/Page/Content/Templates/FQAdminPanel/FQAdminContext';
import fr from 'facet-rest';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useModalContextSubmissionSuccessful, useRefreshModalItemDeleted } from 'Hooks/useRefreshEvent';

const AdminItemTable = () => {
  const { pageContentResource } = usePageContentContext();
  const {
    loadModalCollectionByLink,
    setModalCollection,
  } = useModalContext();

  const { displayErrorMessage } = useSnackbarContext();

  const [filters, setFilters] = useState({});
  const { listPrompt, listPromptSingular, createRelationshipName } = useFQAdminContext();

  const {
    collection,
    refreshCollection,
    setCollection,
  } = usePaginatedCollectionStateContext();

  useModalContextSubmissionSuccessful(() => {
    refreshCollection();
  }, [refreshCollection]);

  useRefreshModalItemDeleted(() => {
    refreshCollection();
  }, [refreshCollection]);

  const onClickAdd = () => pageContentResource.withRelationship(createRelationshipName, async (rel) => {
    const resp = await rel.link('related').withQueryParams().limit(0).get();
    resp.create({
      addToCollection: true,
    });
    setModalCollection(resp);
  }, () => null);

  const handleFilter = async (link, fieldName, fieldValue) => {
    if (fieldName && fieldValue) {
      try {
        const result = await link.copy().withQueryParams().equal(fieldName, fieldValue).get();
        setCollection(result);
      } catch (err) {
        displayErrorMessage(err, err.message || 'Failed to search');
      }
    } else {
      // refetch the collection without filters
      const result = await link.copy().get();
      setCollection(result);
    }
  };

  const renderCell = (res, field) => {
    let cellContent = null;

    switch (field) {
      case 'id':
        cellContent = res.id();
        break;
      case 'edit':
        cellContent = res.withRelationship(fr.relationships.edit.name, (relationship) => (
          <FDSecondaryActionIconButton
            icon={'edit'}
            label={relationship.meta('prompt')}
            onClick={() => loadModalCollectionByLink(relationship.link('related').url())}
          />
        ), () => {
          if (res.meta().canPut()) {
            return (
              <FDClickableText
                appearance={'minor'}
                label={'Edit'}
                onClick={() => loadModalCollectionByLink(res.link('self').url())}
              />
            );
          }
          return null;
        });
        break;
      case 'preview':
        cellContent = res.withRelationship(fr.relationships.preview.name, (relationship) => (
          <FDSecondaryActionIconButton
            icon={'preview'}
            label={relationship.meta('prompt')}
            onClick={() => window.open(relationship.link('related').url(), '_blank')}
          />
        ), () => null);
        break;
      default:
        cellContent = res.attr(field);
    }

    return <FDTableCell>{cellContent}</FDTableCell>;
  };

  const renderTable = () => {
    // hide some fields to make admin more usable.
    // This is a hack until we're able to create AdminItemRow types for each of the new types (payment page, completed screen, etc.)
    const fieldsToFilterOut = [
      'title', 'conditionId', // Unwanted fields on Payment Pages
      'completedTitle', 'description', 'conditionId', 'bannerText', 'generalDisclosure', 'bannerId', // Unwanted fields on completed screens
    ];
    const resourceFields = collection.meta().template().list().filter(field => !fieldsToFilterOut.includes(field.attr('name')));
    const resourceFieldNames = resourceFields.map(field => field.attr('name'));

    // add relationships to field names
    const itemRelationships = collection.resources().head().relationships().itemMap;
    const fieldNames = ['id', ...resourceFieldNames, ...Object.keys(itemRelationships)];

    // build headings array
    const resourceHeadings = resourceFields.map(field => field.attr('prompt'));
    const headingNames = ['ID', ...resourceHeadings, ...Object.values(itemRelationships).map(rel => rel.meta('prompt'))];

    if (!fieldNames.includes('edit')) {
      // if we don't have an edit relationship, the table is displaying the item directly instead of the `AdminItemTableRow` thing. Add edit manually.
      fieldNames.push('edit');
      headingNames.push('Edit');
    }

    return (
      <FDTable size={'sm'}>
        <FDTableHead>
          {headingNames.map((h) => <FDTableCell key={h}>{h}</FDTableCell>)}
        </FDTableHead>
        <FDTableBody>
          {collection.resources()?.list().map((res) => (
            <FDTableRow key={res.id()}>
              {fieldNames.map(field => renderCell(res, field))}
            </FDTableRow>
          ))}
        </FDTableBody>
      </FDTable>
    );
  };

  const renderFilterFields = () => {
    const queryName = collection.meta().queries().head()?.attr('rel');

    return collection.query(queryName, (link) => {
      const allFields = collection.meta().queries().head().template()
        .list();

      // Don't render inputs for sort, limit, and offset fields
      const filterFields = allFields.filter((field) => !['sort', 'limit', 'offset'].includes(field.attr('name')));

      return (
        <>
          {filterFields.map((field) => {
            const fieldName = field.attr('name');
            return (
              <FDSearchInput
                key={fieldName}
                value={filters[fieldName]}
                onChange={(e) => setFilters((prev) => ({ ...prev, [fieldName]: e.target.value }))}
                onSearch={() => handleFilter(link, fieldName, filters[fieldName])}
                placeholder={field.attr('prompt') || field.attr('name')}
              />
            );
          })}
        </>
      );
    }, () => null);
  };

  return (
    <FDContentContainer maxWidth={'lg'}>
      <FDPaper appearance={'elevated'} padding={2}>
        <FDStack direction={'column'} spacing={4}>
          <FDStack direction={'column'} spacing={2}>
            <FDStack justifyContent={'space-between'}>
              <FDHeadlineText value={listPrompt} />
              <FDStack justifyContent={'space-between'}>
                <FDPrimaryActionIconButton
                  icon={'add'}
                  alignSelf={'flex-end'}
                  label={`Add ${listPromptSingular}`}
                  onClick={onClickAdd}
                  disabled={!onClickAdd}
                />
              </FDStack>
            </FDStack>
            { renderFilterFields() }
          </FDStack>
          {collection.resources().empty() ? (
            <FDBox padding={8}>
              <FDEmptyState icon={'search'} title={'No results'} description={'Either none exist or your filters are too strict.'} />
            </FDBox>
          ) : renderTable()}
        </FDStack>
      </FDPaper>
    </FDContentContainer>
  );
};

export { AdminItemTable };
